import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus,
  FaEdit,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaTrash,
  FaPrint,
  FaFileExcel,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import {
  getTaxes,
  deleteTax,
  toggleTaxStatus,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import usePdfExport from '../../../../shared/pdf/usePdfExport';
import PdfExportManager from '../../../../shared/pdf/PdfExportManager';
import { exportRowsToExcel } from '../../../../shared/utils.js/excelExport';

const COLORS = {
  primary: '#11324D',
  accent: '#AC2000',
};

const SEARCH_DEBOUNCE_MS = 300;
const SORT_FIELDS = {
  INDEX: '__index',
  NAME_AR: 'name_ar',
  NAME_EN: 'name_en',
  RATE: 'rate',
  STATUS: 'is_active',
};

const Taxes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const {
    taxes: taxesFromStore,
    taxesPagination,
    isLoading,
    error,
    success,
  } = useSelector(s => s.auth);
  const [taxes, setTaxes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const searchTimeoutRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [deleteId, setDeleteId] = useState(null);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    setTitle(t('taxes.title'));
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [setTitle, dispatch, t]);

  useEffect(() => {
    const params = { page: currentPage, per_page: perPage };
    if (searchTerm) params.search = searchTerm;
    dispatch(getTaxes(params));
  }, [dispatch, currentPage, searchTerm, perPage]);

  // Sync taxes from store to local state (needed for optimistic toggle)
  useEffect(() => {
    setTaxes(Array.isArray(taxesFromStore) ? taxesFromStore : []);
  }, [taxesFromStore]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      dispatch(
        getTaxes({
          page: currentPage,
          per_page: perPage,
          search: searchTerm || undefined,
        }),
      );
    }
  }, [success, dispatch, currentPage, searchTerm, perPage]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch]);

  // Cleanup pending search debounce on unmount
  useEffect(
    () => () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    },
    [],
  );

  const pagination = taxesPagination || { current_page: 1, last_page: 1 };
  const items = useMemo(() => (Array.isArray(taxes) ? taxes : []), [taxes]);
  const pageOffset = ((pagination?.current_page || 1) - 1) * perPage;

  const handleSearchChange = value => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    // Debounced: one API request after the user stops typing (via searchTerm)
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSort = field => {
    if (sortField === field) {
      setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = field => {
    if (sortField !== field)
      return (
        <FaSort
          style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }}
        />
      );
    return sortOrder === 'asc' ? (
      <FaSortUp
        style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }}
      />
    ) : (
      <FaSortDown
        style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }}
      />
    );
  };

  const sortedItems = useMemo(() => {
    if (!sortField) return items;
    if (sortField === SORT_FIELDS.INDEX) {
      return sortOrder === 'asc' ? items : [...items].reverse();
    }
    return [...items].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
      if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [items, sortField, sortOrder]);

  // ---- Shared PDF / Excel column config -------------------------------
  const formatRate = tax => (tax.rate != null ? `${Number(tax.rate)}%` : '—');
  const formatStatus = tax =>
    tax.is_active ? t('taxes.status.active') : t('taxes.status.inactive');

  const exportColumns = useMemo(
    () => [
      { key: 'name_ar', label: t('taxes.tableHeaders.nameAr'), align: 'start' },
      { key: 'name_en', label: t('taxes.tableHeaders.nameEn') },
      {
        key: 'rate',
        label: t('taxes.tableHeaders.rate'),
        render: formatRate,
        excelValue: formatRate,
      },
      {
        key: 'is_active',
        label: t('taxes.tableHeaders.status'),
        render: formatStatus,
        excelValue: formatStatus,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isRTL],
  );

  const pdf = usePdfExport({
    idPrefix: 'taxes',
    isRTL,
    fileNamePrefix: 'ACE_Taxes',
    onError: err =>
      toast.error(
        err?.message === 'NO_DATA'
          ? t('taxes.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' })
          : t('taxes.print.error', {
              defaultValue: 'حدث خطأ أثناء إنشاء PDF',
            }),
        { rtl: isRTL },
      ),
  });

  const handleExportAllToExcel = () => {
    if (!sortedItems || sortedItems.length === 0) {
      toast.error(
        t('taxes.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }),
        { rtl: isRTL },
      );
      return;
    }
    exportRowsToExcel({
      columns: exportColumns,
      rows: sortedItems,
      fileName: t('taxes.excel.fileName'),
      sheetName: t('taxes.excel.sheetName'),
      startIndex: pageOffset,
      rtl: isRTL,
    });
    toast.success(
      t('taxes.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }),
      { rtl: isRTL },
    );
  };

  const handleExportTaxToExcel = tax => {
    exportRowsToExcel({
      columns: exportColumns,
      rows: [tax],
      fileName: `${isRTL ? 'ضريبة' : 'Tax'}_${tax.name_ar || tax.name_en || tax.id}`,
      sheetName: isRTL ? 'ضريبة' : 'Tax',
      rtl: isRTL,
    });
    toast.success(
      t('taxes.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }),
      { rtl: isRTL },
    );
  };

  const handleApplyPerPage = () => {
    const total = pagination?.total || 0;
    const maxAllowed = total > 0 ? total : 1000;
    const next = Math.min(Math.max(1, parseInt(perPageInput, 10) || 1), maxAllowed);
    setPerPage(next);
    setPerPageInput(next);
    setCurrentPage(1);
  };

  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  return (
    <div
      style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        direction: dir,
      }}
    >
      <h1 style={{ margin: '10px 0', fontSize: '20px', textAlign }}>
        {t('taxes.title')}
      </h1>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            <label
              style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}
            >
              {t('common.perPage', { defaultValue: 'Per Page' })}:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setPerPageInput(v => Math.max(1, v - 1))}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '6px 0 0 6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  minWidth: '32px',
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max={pagination?.total || 1000}
                value={perPageInput}
                onChange={e => {
                  const value = parseInt(e.target.value, 10) || 1;
                  const maxValue = pagination?.total || 1000;
                  setPerPageInput(Math.max(1, Math.min(maxValue, value)));
                }}
                style={{
                  width: '70px',
                  padding: '6px 10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderRadius: 0,
                  color: 'white',
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              />
              <button
                onClick={() =>
                  setPerPageInput(v =>
                    Math.min(pagination?.total || 1000, v + 1),
                  )
                }
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '0 6px 6px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  minWidth: '32px',
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: COLORS.accent,
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}
            >
              {t('common.apply', { defaultValue: 'تطبيق' })}
            </button>
          </div>
          <button
            onClick={() => pdf.previewAll(sortedItems)}
            style={{
              backgroundColor: '#444',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              direction: dir,
              textAlign,
            }}
          >
            <FaEye /> {t('taxes.preview.all')}
          </button>
          <button
            onClick={() => pdf.printAll(sortedItems)}
            disabled={pdf.isGeneratingPdf}
            style={{
              backgroundColor: pdf.isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: pdf.isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: pdf.isGeneratingPdf ? 0.6 : 1,
              direction: dir,
              textAlign,
            }}
          >
            <FaPrint />{' '}
            {pdf.isGeneratingPdf
              ? t('taxes.print.generating')
              : t('taxes.print.all')}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              direction: dir,
              textAlign,
            }}
          >
            <FaFileExcel /> {t('taxes.export.all')}
          </button>
          <button
            onClick={() => navigate('/taxes/add')}
            style={{
              backgroundColor: COLORS.accent,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              direction: dir,
              textAlign,
            }}
          >
            <FaPlus /> {t('taxes.add')}
          </button>
        </div>
      </div>

      <div
        style={{
          background: '#1b3350',
          border: '1px solid #333',
          borderRadius: 12,
          padding: 12,
          marginBottom: 16,
        }}
      >
        <input
          type="text"
          placeholder={t('taxes.search.placeholder')}
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
          aria-label={t('taxes.search.placeholder')}
          style={{
            width: '100%',
            padding: '8px 12px',
            backgroundColor: '#1b3350',
            border: '1px solid #333',
            borderRadius: 8,
            color: 'white',
            fontSize: 14,
          }}
        />
      </div>

      <div
        style={{
          overflowX: 'auto',
          background: '#1b3350',
          border: '1px solid #333',
          borderRadius: 12,
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '900px',
            tableLayout: 'auto',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: COLORS.accent }}>
              <th
                style={{ ...headerStyle, cursor: 'pointer' }}
                onClick={() => handleSort(SORT_FIELDS.INDEX)}
              >
                #{getSortIcon(SORT_FIELDS.INDEX)}
              </th>
              <th
                style={{ ...headerStyle, cursor: 'pointer' }}
                onClick={() => handleSort(SORT_FIELDS.NAME_AR)}
              >
                {t('taxes.tableHeaders.nameAr')}
                {getSortIcon(SORT_FIELDS.NAME_AR)}
              </th>
              <th
                style={{ ...headerStyle, cursor: 'pointer' }}
                onClick={() => handleSort(SORT_FIELDS.NAME_EN)}
              >
                {t('taxes.tableHeaders.nameEn')}
                {getSortIcon(SORT_FIELDS.NAME_EN)}
              </th>
              <th
                style={{ ...headerStyle, cursor: 'pointer' }}
                onClick={() => handleSort(SORT_FIELDS.RATE)}
              >
                {t('taxes.tableHeaders.rate')}
                {getSortIcon(SORT_FIELDS.RATE)}
              </th>
              <th
                style={{ ...headerStyle, cursor: 'pointer' }}
                onClick={() => handleSort(SORT_FIELDS.STATUS)}
              >
                {t('taxes.tableHeaders.status')}
                {getSortIcon(SORT_FIELDS.STATUS)}
              </th>
              <th style={headerStyle}>{t('taxes.tableHeaders.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: 20 }}>
                  {t('common.loading', { defaultValue: 'جاري التحميل...' })}
                </td>
              </tr>
            )}
            {!isLoading && sortedItems.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: 20 }}>
                  {t('taxes.noData')}
                </td>
              </tr>
            )}
            {!isLoading &&
              sortedItems.map((tax, index) => (
                <tr key={tax.id}>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {pageOffset + index + 1}
                  </td>
                  <td style={{ ...cellStyle, minWidth: 200 }}>
                    {tax.name_ar || '—'}
                  </td>
                  <td style={{ ...cellStyle, minWidth: 200 }}>
                    {tax.name_en || '—'}
                  </td>
                  <td style={cellStyle}>{formatRate(tax)}</td>
                  <td style={cellStyle}>
                    <span
                      style={{
                        backgroundColor: tax.is_active
                          ? COLORS.primary
                          : '#3a3f50',
                        color: 'white',
                        padding: '6px 16px',
                        borderRadius: 18,
                        fontSize: 12,
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        minWidth: 100,
                        justifyContent: 'center',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        direction: dir,
                        textAlign: 'center',
                      }}
                    >
                      {formatStatus(tax)}
                    </span>
                  </td>
                  <td
                    style={{
                      ...cellStyle,
                      textAlign: 'center',
                      minWidth: '200px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'nowrap',
                        justifyContent: 'center',
                      }}
                    >
                      <ActionButton
                        title={t('taxes.actions.view')}
                        onClick={() => pdf.previewSingle(tax)}
                      >
                        <FaEye />
                      </ActionButton>
                      <ActionButton
                        title={t('taxes.actions.print')}
                        onClick={() =>
                          pdf.printSingle(
                            tax,
                            tax.name_ar || tax.name_en || tax.id,
                          )
                        }
                        disabled={pdf.isGeneratingPdf}
                      >
                        <FaPrint />
                      </ActionButton>
                      <ActionButton
                        title={t('taxes.actions.export')}
                        onClick={() => handleExportTaxToExcel(tax)}
                        bg="#28a745"
                      >
                        <FaFileExcel />
                      </ActionButton>
                      <ActionButton
                        title={t('taxes.actions.edit')}
                        onClick={() => navigate(`/taxes/edit/${tax.id}`)}
                      >
                        <FaEdit />
                      </ActionButton>
                      <ActionButton
                        title={t('taxes.actions.toggleStatus')}
                        bg="#34495e"
                        onClick={async () => {
                          // Optimistic UI: update is_active locally
                          const wasActive = tax.is_active;
                          setTaxes(
                            items.map(item =>
                              item.id === tax.id
                                ? { ...item, is_active: !item.is_active }
                                : item,
                            ),
                          );
                          try {
                            await dispatch(toggleTaxStatus(tax.id)).unwrap();
                            dispatch(clearSuccess());
                            dispatch(
                              getTaxes({
                                page: currentPage,
                                per_page: perPage,
                                search: searchTerm || undefined,
                              }),
                            );
                          } catch {
                            // Rollback on error
                            setTaxes(
                              items.map(item =>
                                item.id === tax.id
                                  ? { ...item, is_active: wasActive }
                                  : item,
                              ),
                            );
                            dispatch(clearError());
                          }
                        }}
                      >
                        {tax.is_active ? <FaToggleOn /> : <FaToggleOff />}
                      </ActionButton>
                      <ActionButton
                        title={t('taxes.actions.delete')}
                        bg="#c0392b"
                        onClick={() => setDeleteId(tax.id)}
                      >
                        <FaTrash />
                      </ActionButton>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {pagination &&
        (() => {
          const current_page = pagination.current_page || 1;
          const last_page = pagination.last_page || 1;
          const pages = [];
          for (let i = 1; i <= last_page; i += 1) {
            if (
              i === 1 ||
              i === last_page ||
              (i >= current_page - 2 && i <= current_page + 2)
            ) {
              pages.push(i);
            } else if (i === current_page - 3 || i === current_page + 3) {
              pages.push('...');
            }
          }
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '20px',
              }}
            >
              <button
                onClick={() => setCurrentPage(current_page - 1)}
                disabled={current_page === 1}
                style={{
                  ...paginationButton(current_page === 1),
                  direction: dir,
                  textAlign,
                }}
              >
                {t('btns.previous')}
              </button>
              {pages.map((page, idx) =>
                page === '...' ? (
                  <span
                    key={`ellipsis-${idx}`}
                    style={{ padding: '8px', color: 'white' }}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={pageButton(page === current_page)}
                  >
                    {page}
                  </button>
                ),
              )}
              <button
                onClick={() => setCurrentPage(current_page + 1)}
                disabled={current_page === last_page}
                style={{
                  ...paginationButton(current_page === last_page),
                  direction: dir,
                  textAlign,
                }}
              >
                {t('btns.next')}
              </button>
            </div>
          );
        })()}

      {deleteId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: '#1b3350',
              border: '1px solid #333',
              borderRadius: 12,
              padding: 20,
              width: 380,
              maxWidth: '95%',
            }}
          >
            <h3
              style={{
                color: 'white',
                margin: '0 0 12px',
                fontSize: '18px',
                direction: dir,
                textAlign,
              }}
            >
              {t('taxes.delete.title')}
            </h3>
            <p style={{ color: '#ccc', direction: dir, textAlign }}>
              {t('taxes.delete.message')}
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
                marginTop: 12,
              }}
            >
              <button
                onClick={() => setDeleteId(null)}
                style={{
                  background: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: 6,
                  direction: dir,
                  textAlign,
                }}
              >
                {t('taxes.delete.cancel')}
              </button>
              <button
                onClick={async () => {
                  try {
                    await dispatch(deleteTax(deleteId)).unwrap();
                    dispatch(clearSuccess());
                    setDeleteId(null);
                    dispatch(
                      getTaxes({
                        page: currentPage,
                        per_page: perPage,
                        search: searchTerm || undefined,
                      }),
                    );
                  } catch {
                    dispatch(clearError());
                  }
                }}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: 6,
                  direction: dir,
                  textAlign,
                }}
              >
                {t('taxes.delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared PDF Engine — measurement, preview, print & single-record */}
      <PdfExportManager
        pdf={pdf}
        title={t('taxes.title')}
        columns={exportColumns}
        rows={sortedItems}
        numberingOffset={pageOffset}
      />
    </div>
  );
};

const headerStyle = {
  padding: 12,
  borderBottom: '1px solid #333',
  color: 'white',
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

const cellStyle = {
  padding: 12,
  borderBottom: '1px solid #333',
  textAlign: 'center',
  whiteSpace: 'normal',
  fontSize: 14,
  lineHeight: 1.6,
  verticalAlign: 'middle',
};

const paginationButton = disabled => ({
  padding: '8px 12px',
  backgroundColor: disabled ? '#333' : '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const pageButton = active => ({
  padding: '8px 12px',
  backgroundColor: active ? '#AC2000' : '#1a1f2e',
  color: 'white',
  border: '1px solid #333',
  borderRadius: '4px',
  cursor: 'pointer',
});

const ActionButton = ({ children, onClick, title, bg = '#2f3644', disabled = false }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    aria-label={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 10px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '38px',
    }}
  >
    {children}
  </button>
);

export default Taxes;
